// src/pages/AssessmentResults.jsx
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAssessment } from '../contexts/AssessmentContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Divider,
  LinearProgress
} from '@mui/material';

import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

export default function AssessmentResults() {
  const navigate = useNavigate();
  const location = useLocation();

  // Original variables (kept commented for future API use)
  // const { submissionData, assessmentSummary } = location.state ?? {};

  const { clearAssessmentData } = useAssessment();

  useEffect(() => {
    // API-related redirects disabled since no API is used now
  }, []);

  // Original helper (kept for future)
  const getMaturityLevelColor = (level) => {
    const colorMap = {
      BASIC: 'error',
      INTERMEDIATE: 'warning',
      ADVANCED: 'success',
      EXPERT: 'info'
    };
    return colorMap[level] ?? 'default';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* ================= HEADER ================= */}
      <Box sx={{ bgcolor: '#4caf50', color: 'white', py: 4 }}>
        <Container>
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 60, mb: 2 }} />

            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700, 
                mb: 1,
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              Assessment Complete!
            </Typography>

            {/* NEW MESSAGE */}
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                fontWeight: 500,
                color: '#e9ffe9',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              We will get back to you with a comprehensive assessment soon.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 6 }}>

        {/* ================= SCORE SECTION ================= */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 4,
            textAlign: 'center',
            bgcolor: 'rgba(0, 68, 148, 0.05)',
            border: '1px solid rgba(0, 68, 148, 0.2)'
          }}
        >
          <Typography
            variant="h5"
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            Your RAISE Index Score
          </Typography>

          <Typography
            variant="h4"
            sx={{
              color: '#004494',
              fontWeight: 700,
              mb: 1,
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            Coming Soon
          </Typography>
        </Paper>

        {/* ================= ASSESSMENT SUMMARY ================= */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography
            variant="h6"
            sx={{ 
              mb: 3, 
              fontWeight: 600, 
              textAlign: 'center',
              fontFamily: 'Helvetica, Arial, sans-serif'
            }}
          >
            Assessment Summary
          </Typography>

          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography
              variant="h4"
              sx={{ 
                fontWeight: 700, 
                color: '#004494',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}
            >
              Coming Soon
            </Typography>
          </Box>
        </Paper>

        {/* ================= ACTION BUTTONS ================= */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            size="large"
            sx={{
              backgroundColor: '#004494',
              color: 'white',
              fontFamily: 'Helvetica, Arial, sans-serif',
              '&:hover': {
                backgroundColor: '#003366',
                color: 'white'
              }
            }}
          >
            Back to Home
          </Button>

          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => {
              sessionStorage.removeItem('assessmentData');
              sessionStorage.removeItem('sessionToken');
              sessionStorage.removeItem('assessmentId');

              clearAssessmentData();
              navigate('/assessment/instant');
            }}
            size="large"
            sx={{
              borderColor: '#004494',
              color: '#004494',
              fontFamily: 'Helvetica, Arial, sans-serif',
              '&:hover': {
                borderColor: '#003366',
                color: '#003366',
                backgroundColor: 'rgba(0, 68, 148, 0.04)'
              }
            }}
          >
            Start New Assessment
          </Button>
        </Box>

        {/* ================= PRESERVED API CODE (COMMENTED OUT) ================= */}
        {/*
        Entire original API-driven score, rating, principle breakdown,
        and summary code is preserved here for future reactivation.
        */}
      </Container>
    </Box>
  );
}